import React, { useCallback, useMemo, useState } from 'react'
import { ArrayFieldValue } from '../FilterFields/types'
import { targetComponents } from './SelectAndLink.constants'
import {
  SelectAndLinkContextValue,
  SelectAndLinkSession,
  SelectAndLinkSessionConfig
} from './SelectAndLink.types'
import SelectAndLinkCard from './SelectAndLinkCard'

export const SelectAndLinkContext =
  React.createContext<SelectAndLinkContextValue | null>(null)

type SelectAndLinkProviderProps = {
  children: React.ReactNode
}

const emptySession: SelectAndLinkSession = {
  onSelect: () => undefined,
  targetField: '',
  targetGroup: '',
  objectclassUuid: '',
  objectClassTitle: '',
  targetComponent: targetComponents.FILTER_FIELDS,
  selectedOptions: [],
  isCompleted: false
}

export function SelectAndLinkProvider({
  children
}: SelectAndLinkProviderProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [session, setSession] = useState<SelectAndLinkSession>(emptySession)

  const onSelect = useCallback((values: ArrayFieldValue) => {
    setSession((prevSession) => ({
      ...prevSession,
      isCompleted: true,
      selectedOptions: [...values]
    }))
  }, [])

  const onClearSelection = useCallback(() => {
    setSession(emptySession)
  }, [])

  const openSelectAndLink = useCallback(
    (config: SelectAndLinkSessionConfig) => {
      const newSession: SelectAndLinkSession = {
        ...emptySession,
        ...config,
        urlSelectedOptions: config.selectedOptions ?? [],
        selectedOptions: [],
        onSelect
      }

      setSession(newSession)

      setIsOpen(true)
    },
    [onSelect]
  )

  const closeSelectAndLink = useCallback(() => {
    setIsOpen(false)
  }, [])

  const value: SelectAndLinkContextValue = useMemo(
    () => ({
      openSelectAndLink,
      closeSelectAndLink,
      onClearSelection,
      isOpen,
      session
    }),
    [openSelectAndLink, closeSelectAndLink, onClearSelection, isOpen, session]
  )

  return (
    <SelectAndLinkContext.Provider value={value}>
      {children}
      {isOpen && (
        <SelectAndLinkCard
          session={session}
          onClose={closeSelectAndLink}
          onSelect={onSelect}
          onCancel={onClearSelection}
        />
      )}
    </SelectAndLinkContext.Provider>
  )
}
